import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  CheckCircle as PaidIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon,
  WarningAmber as WarningIcon,
  AccessTime as AccessTimeIcon,
  WorkHistory as WorkHistoryIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  PlaylistAddCheck as GenerateAllIcon,
  AccountBalance as VirementIcon,
  Assignment as DeclarationIcon,
  TableChart as BaseCalculIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircleOutline as ReadyIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { employeesAPI, employeePrimesAPI, paieAPI, acomptesAPI, pretsAPI, bulletinsPaieAPI, auditLogsAPI } from '../../../services/api';
import { Employee, EmployeeDropdownItem } from '../../../types/employee';
import { PayrollCalculation, CNSS_CEILING, calculateMonthlyPayroll, primeAppliesToMonth } from '../../../types/paie';
import { PretPaiementInfo, AcompteInfo, PointageSummary, AncienneteInfo } from '../../../services/api';
import { BulletinPaie, GenerateAllBulletinsResponse } from '../../../types/bulletinPaie';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
// Heavy PDF / Excel generators (jspdf, exceljs) are dynamically imported in
// each handler so their chunks only load when the user clicks the matching
// action. Keeping them out of the main bundle is the biggest single win for
// first-paint performance.

const HR_COLOR = '#00897B';

// Signalement d'obsolescence des bulletins — désactivé temporairement à la
// demande. Couvre le bandeau de la fiche ET le repère orange dans la liste des
// employés : le code reste en place, repasser à true réaffiche les deux d'un
// coup. Le backend n'est pas touché — /drift répond toujours.

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// A generated bulletin is a settled document: read what it stored, never
// recompute it. Recomputing meant any later edit — a prime added, a salary
// raised, a seniority threshold crossed — silently re-rated months that were
// already closed, and the page then disagreed with the payslip already issued.
// See Docs/BULLETIN_SNAPSHOT_PLAN.md.
const payrollFromBulletin = (b: BulletinPaie): PayrollCalculation => {
  const n = (v: any) => Number(v) || 0;
  const lines = (b.primes_non_exonerees_detail || []).map(l => ({
    nom: l.nom, montant: n(l.montant), prorate: l.prorate !== false,
  }));
  const linesExo = (b.primes_exonerees_detail || []).map(l => ({
    nom: l.nom, montant: n(l.montant), prorate: l.prorate !== false,
  }));
  const sbi = n(b.salaire_brut_imposable);
  return {
    salaireBase: n(b.salaire_base),
    totalPrimesNonExonerees: n(b.total_primes_non_exonerees),
    primesNonExonereesDetails: lines,
    totalPrimesExonerees: n(b.total_primes_exonerees),
    primesExonereesDetails: linesExo,
    salaireBrutGlobal: n(b.salaire_brut_global),
    salaireBrutImposable: sbi,
    cotisationsDetails: (b.cotisations_detail || []).map(c => {
      const isCNSS = (c.nom || '').toUpperCase().includes('CNSS');
      const base = (c as any).base !== undefined && (c as any).base !== null
        ? n((c as any).base)
        : (isCNSS ? Math.min(sbi, CNSS_CEILING) : sbi);
      return {
        nom: c.nom,
        taux: n(c.taux),
        montant: (c as any).montant !== undefined && (c as any).montant !== null
          ? n((c as any).montant)
          : base * n(c.taux) / 100,
        isCNSS,
        baseForCalculation: base,
      };
    }),
    totalCotisations: n(b.total_cotisations),
    fraisProfessionnel: n(b.frais_professionnel),
    fraisProfessionnelRate: sbi <= 6500 ? 0.35 : 0.25,
    fraisProfessionnelMax: sbi <= 6500 ? 2500 : 2916.67,
    salaireNetImposable: n(b.salaire_net_imposable),
    salaireNetImposableAnnuel: n(b.salaire_net_imposable) * 12,
    irBrut: n(b.ir_brut),
    irBracket: undefined,
    nbrPersonnesACharge: n(b.nbr_personnes_a_charge),
    deductionChargeFamille: n(b.deduction_charge_famille),
    ir: n(b.ir_net),
    netAPayer: n(b.salaire_net),
    // Monthly detail, as frozen on the bulletin
    deductionAbsence: n(b.deduction_absence),
    gainJourFerie: n(b.gain_jour_ferie),
    gainConge: n(b.gain_conge),
    primeAnciennete: n(b.prime_anciennete),
    tauxAnciennete: n(b.taux_anciennete),
    heuresAbsence: n(b.heures_absence),
    joursFerie: n(b.jours_ferie),
    joursConge: n(b.jours_conge),
    heuresPresence: n(b.heures_presence),
    totalPrimesExonereesProratees: n(b.total_primes_exonerees),
    totalPrimesNonExonereesProratees: n(b.total_primes_non_exonerees),
    prorationFactor: n(b.proration_factor),
  };
};

const BaseCalculPaieManagement: React.FC = () => {
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeDropdownItem[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDropdownItem | null>(null);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState<Employee | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Month navigator
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Payroll data
  const [payroll, setPayroll] = useState<PayrollCalculation | null>(null);
  const [activePret, setActivePret] = useState<PretPaiementInfo | null>(null);
  const [activeAcomptes, setActiveAcomptes] = useState<AcompteInfo[]>([]);
  const [pointageSummary, setPointageSummary] = useState<PointageSummary | null>(null);
  const [ancienneteInfo, setAncienneteInfo] = useState<AncienneteInfo | null>(null);

  // Bulletin state
  const [bulletinStatusMap, setBulletinStatusMap] = useState<Record<string, BulletinPaie>>({});
  // Month-wide staleness: which generated bulletins no longer match the data.
  // Computed for the whole month so it is visible without opening anyone.
  const [generatingAll, setGeneratingAll] = useState(false);

  // Pre-check state
  const [preCheckData, setPreCheckData] = useState<{
    total_employees: number;
    ready: number;
    not_ready: number;
    already_exists: number;
    details: Array<{
      employee_id: number;
      employee_name: string;
      matricule: string;
      status: 'ready' | 'error' | 'exists';
      reason?: string;
    }>;
  } | null>(null);
  const [showPreCheckDialog, setShowPreCheckDialog] = useState(false);
  const [preCheckLoading, setPreCheckLoading] = useState(false);
  const [preCheckExpanded, setPreCheckExpanded] = useState(false);

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // Load active employees (using dropdown endpoint - no pagination needed)
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await employeesAPI.getAllForDropdown();
      const nonStagiaires = response.filter(emp => emp.type_contrat !== 'STAGE');
      setEmployees(nonStagiaires);
      setFilteredEmployees(nonStagiaires);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Filter employees by search
  useEffect(() => {
    if (!search) {
      setFilteredEmployees(employees);
      return;
    }
    const searchLower = search.toLowerCase();
    setFilteredEmployees(
      employees.filter(
        (emp) =>
          emp.nom_complet.toLowerCase().includes(searchLower) ||
          emp.matricule.toLowerCase().includes(searchLower) ||
          (emp.fonction && emp.fonction.toLowerCase().includes(searchLower))
      )
    );
  }, [search, employees]);

  // Check if selected month is the current month
  const isCurrentMonth = useCallback(() => {
    const now = new Date();
    return selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
  }, [selectedYear, selectedMonth]);

  // Navigate to previous month
  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  // Navigate to next month
  const handleNextMonth = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    // Don't allow navigation beyond current month
    if (selectedYear === currentYear && selectedMonth >= currentMonth) {
      return;
    }

    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Check if can navigate to next month
  const canNavigateNext = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    return !(selectedYear === currentYear && selectedMonth >= currentMonth);
  };

  // Load bulletin status map for all employees in the selected month
  const loadBulletinStatusMap = useCallback(async (year: number, month: number) => {
    try {
      const statusMap = await bulletinsPaieAPI.getMonthStatus(month, year);
      setBulletinStatusMap(statusMap);
    } catch {
      setBulletinStatusMap({});
    }
  }, []);


  // Derive existing bulletin for selected employee from the status map
  const existingBulletin: BulletinPaie | null = selectedEmployee
    ? (bulletinStatusMap[String(selectedEmployee.id)] || null)
    : null;

  // `payroll` stays the LIVE calculation — it is what the drift check compares
  // against. What the page actually renders is the bulletin once one exists.
  const storedPayroll = existingBulletin ? payrollFromBulletin(existingBulletin) : null;
  const displayPayroll = storedPayroll ?? payroll;


  // Load employee payroll details
  const loadEmployeeDetails = useCallback(async (employee: EmployeeDropdownItem, year: number, month: number) => {
    setLoadingDetails(true);
    setPayroll(null);
    setActivePret(null);
    setActiveAcomptes([]);
    setPointageSummary(null);
    setAncienneteInfo(null);
    setSelectedEmployeeDetails(null);

    try {
      // First get full employee details
      const employeeDetails = await employeesAPI.getById(employee.id);
      setSelectedEmployeeDetails(employeeDetails);

      // Load primes and cotisations
      const primesResponse = await employeePrimesAPI.get(employee.id);

      // Load payroll data for the specific month
      const payrollMonthData = await paieAPI.getPayrollMonth(employee.id, year, month);
      setActivePret(payrollMonthData.pret);
      setActiveAcomptes(payrollMonthData.acomptes || []);
      setPointageSummary(payrollMonthData.pointage_summary);
      setAncienneteInfo(payrollMonthData.anciennete);

      // La règle vit dans primeAppliesToMonth() (types/paie.ts), miroir de
      // prime_applies() côté backend, partagé avec le récapitulatif de la fiche.
      const appliesThisMonth = (p: any): boolean => primeAppliesToMonth(p, month, year);

      // Get primes details for display
      const primesExonereesDetails = primesResponse.primes_exonerees
        .filter((p: any) => p.montant > 0 && appliesThisMonth(p))
        .map((p: any) => ({
          nom: p.prime_nom || '',
          montant: Number(p.montant) || 0,
          prorate: p.prorate !== false,
        }));

      const primesNonExonereesDetails = primesResponse.primes_non_exonerees
        .filter((p: any) => p.montant > 0 && appliesThisMonth(p))
        .map((p: any) => ({
          nom: p.prime_nom || '',
          montant: Number(p.montant) || 0,
          prorate: p.prorate !== false,
        }));

      // Get cotisations for calculation
      const cotisationsList = (primesResponse.cotisations_sociales || [])
        .map((c: any) => ({
          nom: c.cotisation_nom || '',
          taux: Number(c.cotisation_taux) || 0,
        }));

      // Only use stored payroll values from employee record
      const hasStoredValues = employeeDetails.salaire_brut_global != null;

      if (hasStoredValues && payrollMonthData.pointage_summary) {
        // Use monthly calculation with pointage data
        // Convert Decimal string fields from API to numbers
        const ps = payrollMonthData.pointage_summary;
        const pointageNumbers = {
          heures_absence: Number(ps.heures_absence) || 0,
          jours_absent: Number(ps.jours_absent) || 0,
          jours_ferie: Number(ps.jours_ferie) || 0,
          jours_conge_paye: Number(ps.jours_conge_paye) || 0,
          jours_conge_maladie: Number(ps.jours_conge_maladie) || 0,
          jours_conge_mariage: Number(ps.jours_conge_mariage) || 0,
          jours_conge_deces: Number(ps.jours_conge_deces) || 0,
          jours_conge_naissance: Number(ps.jours_conge_naissance) || 0,
          heures_presence: Number(ps.heures_presence) || 0,
          jours_present: Number(ps.jours_present) || 0,
          heures_supplementaires: Number(ps.heures_supplementaires) || 0,
        };
        const tauxAnc = payrollMonthData.anciennete ? Number(payrollMonthData.anciennete.taux_anciennete) : 0;
        const monthlyPayroll = calculateMonthlyPayroll(
          Number(employeeDetails.salaire_base) || 0,
          Number(employeeDetails.declaration_journaliere) || 0,
          primesExonereesDetails,
          primesNonExonereesDetails,
          cotisationsList,
          employeeDetails.nbr_enfants || 0,
          employeeDetails.situation_familiale || 'CELIBATAIRE',
          pointageNumbers,
          tauxAnc,
        );
        setPayroll(monthlyPayroll);
      } else if (hasStoredValues) {
        // Fall back to static stored values
        const E = Number(employeeDetails.salaire_brut_imposable) || 0;
        const cotisationsDetails = (primesResponse.cotisations_sociales || [])
          .map((c: any) => {
            const isCNSS = (c.cotisation_nom || '').toUpperCase().includes('CNSS');
            const baseForCalculation = isCNSS ? Math.min(E, CNSS_CEILING) : E;
            return {
              nom: c.cotisation_nom,
              taux: Number(c.cotisation_taux) || 0,
              montant: baseForCalculation * (Number(c.cotisation_taux) || 0) / 100,
              isCNSS,
              baseForCalculation
            };
          });
        const totalPrimesExonerees = primesExonereesDetails.reduce((sum: number, p: any) => sum + p.montant, 0);
        const totalPrimesNonExonerees = primesNonExonereesDetails.reduce((sum: number, p: any) => sum + p.montant, 0);
        const salaireNetImposable = Number(employeeDetails.salaire_net_imposable) || 0;
        const storedPayroll: PayrollCalculation = {
          salaireBase: Number(employeeDetails.salaire_base) || 0,
          totalPrimesExonerees,
          primesExonereesDetails,
          totalPrimesNonExonerees,
          primesNonExonereesDetails,
          salaireBrutGlobal: Number(employeeDetails.salaire_brut_global) || 0,
          salaireBrutImposable: Number(employeeDetails.salaire_brut_imposable) || 0,
          cotisationsDetails,
          totalCotisations: Number(employeeDetails.total_cotisations) || 0,
          fraisProfessionnel: Number(employeeDetails.frais_professionnel) || 0,
          fraisProfessionnelRate: 0.20,
          fraisProfessionnelMax: 2500,
          salaireNetImposable,
          salaireNetImposableAnnuel: salaireNetImposable * 12,
          irBrut: Number(employeeDetails.ir_brut) || 0,
          irBracket: undefined,
          nbrPersonnesACharge: employeeDetails.nbr_enfants + (employeeDetails.situation_familiale === 'MARIE' ? 1 : 0),
          deductionChargeFamille: Number(employeeDetails.deduction_charge_famille) || 0,
          ir: Number(employeeDetails.ir_net) || 0,
          netAPayer: Number(employeeDetails.salaire_net) || 0,
        };
        setPayroll(storedPayroll);
      }
      // If no stored values, payroll remains null - UI will show configuration message
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoadingDetails(false);
    }
  }, [showError]);

  // Handle employee selection
  const handleEmployeeSelect = (employee: EmployeeDropdownItem) => {
    setSelectedEmployee(employee);
    loadEmployeeDetails(employee, selectedYear, selectedMonth);
  };

  // Load bulletin status map on mount and when month changes
  useEffect(() => {
    loadBulletinStatusMap(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth, loadBulletinStatusMap]);

  // Reload data when month changes
  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeDetails(selectedEmployee, selectedYear, selectedMonth);
    }
  }, [selectedYear, selectedMonth]);

  // Handle toggling acompte inclusion in payroll
  const handleToggleAcompte = async (acompteId: number, currentlyIncluded: boolean) => {
    // Optimistic local update — no full reload
    setActiveAcomptes(prev =>
      prev.map(a =>
        a.acompte_id === acompteId
          ? { ...a, inclus_dans_calcul: !currentlyIncluded }
          : a
      )
    );
    try {
      await acomptesAPI.togglePrelevement(acompteId, {
        mois: selectedMonth,
        annee: selectedYear,
        inclus: !currentlyIncluded,
      });
    } catch (error: any) {
      // Revert on failure
      setActiveAcomptes(prev =>
        prev.map(a =>
          a.acompte_id === acompteId
            ? { ...a, inclus_dans_calcul: currentlyIncluded }
            : a
        )
      );
      showError(extractErrorMessage(error));
    }
  };

  // Handle toggling pret inclusion in payroll (same pattern as acompte)
  const handleTogglePret = async (pretId: number, currentlyIncluded: boolean) => {
    // Optimistic local update
    setActivePret(prev =>
      prev ? { ...prev, inclus_dans_calcul: !currentlyIncluded } : prev
    );
    try {
      await pretsAPI.togglePrelevement(pretId, {
        mois: selectedMonth,
        annee: selectedYear,
        inclus: !currentlyIncluded,
      });
    } catch (error: any) {
      // Revert on failure
      setActivePret(prev =>
        prev ? { ...prev, inclus_dans_calcul: currentlyIncluded } : prev
      );
      showError(extractErrorMessage(error));
    }
  };

  // Pre-check then generate all bulletins for the selected month
  const handleGenerateAllBulletins = async () => {
    setPreCheckLoading(true);
    setPreCheckExpanded(false);
    try {
      const data = await bulletinsPaieAPI.preCheck(selectedMonth, selectedYear);
      setPreCheckData(data);
      setShowPreCheckDialog(true);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setPreCheckLoading(false);
    }
  };

  // Confirm generation after pre-check
  const handleConfirmGenerate = async () => {
    setShowPreCheckDialog(false);
    setGeneratingAll(true);
    try {
      const result: GenerateAllBulletinsResponse = await bulletinsPaieAPI.generateAll(selectedMonth, selectedYear);

      // Reload bulletin status map
      const updatedStatusMap = await bulletinsPaieAPI.getMonthStatus(selectedMonth, selectedYear);
      setBulletinStatusMap(updatedStatusMap);

      // Reload current employee details if one is selected
      if (selectedEmployee) {
        loadEmployeeDetails(selectedEmployee, selectedYear, selectedMonth);
      }

      // Generate combined PDF from all bulletins of the month
      const allBulletins = Object.values(updatedStatusMap);
      if (allBulletins.length > 0) {
        openPreview({
          getBlob: async () => {
            const { generateAllBulletinsPDF } = await import('../../../utils/bulletinPaiePdfGenerator');
            return generateAllBulletinsPDF(allBulletins, selectedMonth, selectedYear, true) as Blob;
          },
          fileName: `Bulletins_Paie_${MONTH_NAMES[selectedMonth - 1]}_${selectedYear}.pdf`,
        });
        auditLogsAPI.logPdfDownload('BulletinPaie', 0, `Aperçu PDF tous bulletins - ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`);
      }

      showSuccess(`${result.generated} bulletin(s) généré(s) avec succès`);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setGeneratingAll(false);
    }
  };

  // Delete all bulletins for the month and revert side effects
  const handleModifierBulletins = async () => {
    // Les bulletins figés vont être remplacés par un calcul frais : c'est le seul
    // moment où un écart avec les données actuelles a des conséquences. On le
    // montre ici, employé par employé, plutôt que d'en faire un bandeau permanent.
    let ecarts = '';
    try {
      const d = await bulletinsPaieAPI.getDrift(selectedMonth, selectedYear);
      if (d.a_regenerer > 0) {
        const lignes = d.details
          .map(x => `  • ${x.matricule} ${x.nom_complet} : ${formatCurrency(x.net_stocke)} → ${formatCurrency(x.net_recalcule)} (${x.ecart > 0 ? '+' : ''}${formatCurrency(x.ecart)})`)
          .join('\n');
        const total = d.details.reduce((sum, x) => sum + x.ecart, 0);
        ecarts = `\n\n⚠ ${d.a_regenerer} bulletin(s) ne donneront PAS le même résultat qu'à leur génération `
          + `— les données ont changé depuis :\n\n${lignes}\n\n`
          + `Impact total sur le net : ${total > 0 ? '+' : ''}${formatCurrency(total)}\n`;
      }
    } catch {
      // Le contrôle est un confort : s'il échoue, on ne bloque pas la régénération.
    }

    showConfirm(
      `Cette action supprimera TOUS les bulletins de ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} ainsi que les prélèvements associés (acomptes et prêts). Les acomptes et prêts seront rétablis à leur état précédent.${ecarts}\nVoulez-vous continuer ?`,
      async () => {
        try {
          const result = await bulletinsPaieAPI.deleteMonth(selectedMonth, selectedYear);

          // Reload bulletin status map (will be empty → re-enables "Générer" button)
          const updatedStatusMap = await bulletinsPaieAPI.getMonthStatus(selectedMonth, selectedYear);
          setBulletinStatusMap(updatedStatusMap);

          // Reload current employee details if one is selected
          if (selectedEmployee) {
            loadEmployeeDetails(selectedEmployee, selectedYear, selectedMonth);
          }

          const lines: string[] = [`${result.deleted} bulletin(s) supprimé(s)`];
          if (result.acomptes_reverted > 0) lines.push(`${result.acomptes_reverted} acompte(s) rétabli(s)`);
          if (result.pret_payments_reverted > 0) lines.push(`${result.pret_payments_reverted} paiement(s) de prêt annulé(s)`);
          showSuccess(lines.join(' | '));
        } catch (error: any) {
          showError(extractErrorMessage(error));
        }
      },
      { title: 'Modifier les Bulletins du Mois' }
    );
  };

  // Generate État de Virement: Excel downloads, PDF opens in the preview viewer.
  const handleEtatVirement = async () => {
    try {
      const allBulletins = Object.values(bulletinStatusMap);
      if (allBulletins.length === 0) {
        showError('Aucun bulletin à exporter');
        return;
      }
      const { generateEtatVirementExcel } = await import('../../../utils/etatVirementExcel');
      await generateEtatVirementExcel(allBulletins, selectedMonth, selectedYear);
      openPreview({
        getBlob: async () => {
          const { generateEtatVirementPDF } = await import('../../../utils/etatVirementPdf');
          return generateEtatVirementPDF(allBulletins, selectedMonth, selectedYear, true) as Blob;
        },
        fileName: `Etat_Virement_${MONTH_NAMES[selectedMonth - 1]}_${selectedYear}.pdf`,
      });
      auditLogsAPI.logPdfDownload('EtatVirement', 0, `Aperçu état de virement - ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  // Generate État de Déclaration CNSS/AMO PDF (preview before print/download).
  const handleEtatDeclaration = async () => {
    try {
      const allBulletins = Object.values(bulletinStatusMap);
      if (allBulletins.length === 0) {
        showError('Aucun bulletin à exporter');
        return;
      }
      openPreview({
        getBlob: async () => {
          const { generateEtatDeclarationCnssPDF } = await import('../../../utils/etatDeclarationCnssPdf');
          return generateEtatDeclarationCnssPDF(allBulletins, selectedMonth, selectedYear, true) as Blob;
        },
        fileName: `Declaration_CNSS_${MONTH_NAMES[selectedMonth - 1]}_${selectedYear}.pdf`,
      });
      auditLogsAPI.logPdfDownload('EtatDeclarationCNSS', 0, `Aperçu déclaration CNSS - ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  // Generate Base de Calcul Paie Excel
  const handleBaseCalculExcel = async () => {
    try {
      const allBulletins = Object.values(bulletinStatusMap);

      // Fetch all employee primes in a single bulk request
      const employeeIds = allBulletins.map(b => b.employee_id);
      const bulkResult = await employeePrimesAPI.getBulk(employeeIds);
      const primesMap: Record<number, any> = {};
      employeeIds.forEach(id => { primesMap[id] = bulkResult[String(id)] || { primes_exonerees: [], primes_non_exonerees: [], cotisations_sociales: [], total_exonerees: 0, total_non_exonerees: 0 }; });

      const { generateBaseCalculPaieExcel } = await import('../../../utils/baseCalculPaieExcel');
      await generateBaseCalculPaieExcel(allBulletins, selectedMonth, selectedYear, primesMap);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  // Format currency
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '0,00 DH';
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value) + ' DH';
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Month Navigator */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, gap: 1 }}>
        <IconButton onClick={handlePreviousMonth} sx={{ color: HR_COLOR }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ color: HR_COLOR }} />
          <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>
            {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
          </Typography>
        </Box>
        <IconButton
          onClick={handleNextMonth}
          disabled={!canNavigateNext()}
          sx={{ color: canNavigateNext() ? HR_COLOR : '#ccc' }}
        >
          <ChevronRightIcon />
        </IconButton>
        {!isCurrentMonth() && (
          <Chip label="Mois passé" size="small" sx={{ bgcolor: '#FF9800', color: 'white' }} />
        )}
      </Box>

      {/* Action Navbar */}
      <Paper sx={{ mb: 1, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={Object.keys(bulletinStatusMap).length > 0 ? 'Les bulletins de ce mois ont déjà été générés' : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={(generatingAll || preCheckLoading) ? <CircularProgress size={18} color="inherit" /> : <GenerateAllIcon />}
                onClick={handleGenerateAllBulletins}
                disabled={generatingAll || preCheckLoading || Object.keys(bulletinStatusMap).length > 0}
                sx={{
                  bgcolor: HR_COLOR,
                  '&:hover': { bgcolor: '#00695C' },
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {generatingAll ? 'Génération...' : preCheckLoading ? 'Vérification...' : 'Générer les Bulletins'}
              </Button>
            </span>
          </Tooltip>
          {Object.keys(bulletinStatusMap).length > 0 && (
            <Tooltip title="Télécharger tous les bulletins en PDF">
              <IconButton
                onClick={() => {
                  const bulletins = Object.values(bulletinStatusMap);
                  openPreview({
                    getBlob: async () => {
                      const { generateAllBulletinsPDF } = await import('../../../utils/bulletinPaiePdfGenerator');
                      return generateAllBulletinsPDF(bulletins, selectedMonth, selectedYear, true) as Blob;
                    },
                    fileName: `Bulletins_Paie_${MONTH_NAMES[selectedMonth - 1]}_${selectedYear}.pdf`,
                  });
                  auditLogsAPI.logPdfDownload('BulletinPaie', 0, `Aperçu PDF tous bulletins - ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`);
                }}
                sx={{ color: '#C62828', ml: 0.5 }}
              >
                <PdfIcon />
              </IconButton>
            </Tooltip>
          )}
          {Object.keys(bulletinStatusMap).length > 0 && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleModifierBulletins}
              sx={{
                ml: 1,
                borderColor: '#FF9800',
                color: '#FF9800',
                '&:hover': { borderColor: '#F57C00', bgcolor: '#FFF3E0' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Modifier
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<BaseCalculIcon />}
            disabled={Object.keys(bulletinStatusMap).length === 0}
            onClick={handleBaseCalculExcel}
            sx={{
              borderColor: HR_COLOR,
              color: HR_COLOR,
              '&:hover': { borderColor: '#00695C', bgcolor: `${HR_COLOR}08` },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Base de Calcul Paie
          </Button>
          <Button
            variant="outlined"
            startIcon={<VirementIcon />}
            disabled={Object.keys(bulletinStatusMap).length === 0}
            onClick={handleEtatVirement}
            sx={{
              borderColor: HR_COLOR,
              color: HR_COLOR,
              '&:hover': { borderColor: '#00695C', bgcolor: `${HR_COLOR}08` },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            État de Virement de Salaire
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeclarationIcon />}
            disabled={Object.keys(bulletinStatusMap).length === 0}
            onClick={handleEtatDeclaration}
            sx={{
              borderColor: HR_COLOR,
              color: HR_COLOR,
              '&:hover': { borderColor: '#00695C', bgcolor: `${HR_COLOR}08` },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            État de Déclaration CNSS
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
        {/* Left Panel - Employee List (height driven by the right panel; list scrolls inside) */}
        <Box sx={{ width: '25%', flexShrink: 0, position: 'relative' }}>
          <Paper sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ color: HR_COLOR, mb: 2 }}>
                Employés Actifs ({filteredEmployees.length})
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher par nom, matricule..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: HR_COLOR }} />
                </Box>
              ) : filteredEmployees.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun employé trouvé
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredEmployees.map((employee, index) => (
                    <React.Fragment key={employee.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          selected={selectedEmployee?.id === employee.id}
                          onClick={() => handleEmployeeSelect(employee)}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: `${HR_COLOR}15`,
                              borderLeft: `3px solid ${HR_COLOR}`,
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: `${HR_COLOR}25`,
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: HR_COLOR }}>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {employee.nom_complet}
                                </Typography>
                                {bulletinStatusMap[String(employee.id)] && (
                                  <Tooltip title={`Bulletin ${bulletinStatusMap[String(employee.id)].ref_bulletin || ''} — ${bulletinStatusMap[String(employee.id)].statut}`}>
                                    <DescriptionIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {employee.matricule}
                                </Typography>
                                {employee.fonction && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    • {employee.fonction}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < filteredEmployees.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Right Panel - Payroll Summary (drives the row height) */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ p: 3, minHeight: 'calc(100vh - 250px)' }}>

            {!selectedEmployee ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <PersonIcon sx={{ fontSize: 80, color: 'divider', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Sélectionnez un employé
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cliquez sur un employé pour voir son récapitulatif de paie
                </Typography>
              </Box>
            ) : loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: HR_COLOR }} />
              </Box>
            ) : (
              <>
                {/* Employee Header */}
                <Box sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: HR_COLOR, width: 56, height: 56 }}>
                        <PersonIcon sx={{ fontSize: 32 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={600}>
                          {selectedEmployee.nom_complet}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip label={selectedEmployee.matricule} size="small" sx={{ bgcolor: '#1976D2', color: 'white' }} />
                          {selectedEmployee.fonction && (
                            <Chip label={selectedEmployee.fonction} size="small" variant="outlined" />
                          )}
                          {selectedEmployeeDetails?.salaire_base && (
                            <Chip
                              label={`Salaire: ${formatCurrency(Number(selectedEmployeeDetails.salaire_base))}`}
                              size="small"
                              sx={{ bgcolor: HR_COLOR, color: 'white' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {/* Bulletin Status (PDF icon only) */}
                    {existingBulletin && (
                      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<DescriptionIcon />}
                          label={`${existingBulletin.statut === 'BROUILLON' ? 'Brouillon' : existingBulletin.statut === 'VALIDE' ? 'Validé' : 'Payé'} — ${existingBulletin.ref_bulletin}`}
                          sx={{
                            bgcolor: existingBulletin.statut === 'BROUILLON' ? '#9E9E9E' :
                              existingBulletin.statut === 'VALIDE' ? '#4CAF50' : '#2196F3',
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            py: 2.5,
                            px: 1,
                          }}
                        />
                        <Tooltip title="Télécharger PDF">
                          <IconButton
                            onClick={() => {
                              openPreview({
                                getBlob: async () => {
                                  const { generateBulletinPaiePDF } = await import('../../../utils/bulletinPaiePdfGenerator');
                                  return generateBulletinPaiePDF(existingBulletin, true) as Blob;
                                },
                                fileName: `Bulletin_${existingBulletin.ref_bulletin || existingBulletin.id}.pdf`,
                              });
                              auditLogsAPI.logPdfDownload('BulletinPaie', existingBulletin.id, `Aperçu PDF bulletin - ${existingBulletin.ref_bulletin}`);
                            }}
                            sx={{ color: '#C62828' }}
                          >
                            <PdfIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Pointage Info Bar */}
                {pointageSummary && (
                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f0f7f6',
                    borderRadius: 2,
                    border: `1px solid ${HR_COLOR}30`,
                  }}>
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={`Heures travaillées: ${Number(pointageSummary.heures_presence).toFixed(2)}h`}
                      size="small"
                      sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 500 }}
                    />
                    {pointageSummary.jours_absent > 0 && (
                      <Chip
                        label={`Jours absence: ${pointageSummary.jours_absent}`}
                        size="small"
                        sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 500 }}
                      />
                    )}
                    {pointageSummary.jours_ferie > 0 && (
                      <Chip
                        label={`Jours fériés: ${pointageSummary.jours_ferie}`}
                        size="small"
                        sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 500 }}
                      />
                    )}
                    {(pointageSummary.jours_conge_paye + pointageSummary.jours_conge_mariage + pointageSummary.jours_conge_deces + pointageSummary.jours_conge_naissance) > 0 && (
                      <Chip
                        label={`Jours congé: ${pointageSummary.jours_conge_paye + pointageSummary.jours_conge_mariage + pointageSummary.jours_conge_deces + pointageSummary.jours_conge_naissance}`}
                        size="small"
                        sx={{ bgcolor: '#E8EAF6', color: '#283593', fontWeight: 500 }}
                      />
                    )}
                    {pointageSummary.jours_conge_maladie > 0 && (
                      <Chip
                        label={`Congé maladie: ${pointageSummary.jours_conge_maladie}j`}
                        size="small"
                        sx={{ bgcolor: '#F3E5F5', color: '#6A1B9A', fontWeight: 500 }}
                      />
                    )}
                    {pointageSummary.heures_supplementaires > 0 && (
                      <Chip
                        label={`H. Supp: ${Number(pointageSummary.heures_supplementaires).toFixed(2)}h`}
                        size="small"
                        sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 500 }}
                      />
                    )}
                    {ancienneteInfo && (
                      <Chip
                        icon={<WorkHistoryIcon />}
                        label={`Ancienneté: ${ancienneteInfo.annees}a ${ancienneteInfo.mois}m (${(Number(ancienneteInfo.taux_anciennete) * 100).toFixed(0)}%)`}
                        size="small"
                        sx={{ bgcolor: `${HR_COLOR}20`, color: HR_COLOR, fontWeight: 500 }}
                      />
                    )}
                  </Box>
                )}

                {!pointageSummary && displayPayroll && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Données de pointage non disponibles pour ce mois. Les valeurs affichées sont basées sur la configuration statique.
                    </Typography>
                  </Alert>
                )}

                {/* Where the figures come from. A generated bulletin is settled:
                    the page shows what it stored. Saying so matters — otherwise
                    HR edits pointage, sees nothing move, and assumes the edit
                    failed. */}
                {/* Purement informatif : un bulletin généré est figé, et c'est
                    normal. Dire pourquoi les chiffres ne bougent pas évite qu'on
                    croie une saisie perdue. L'écart avec un calcul frais n'est
                    montré qu'au moment de régénérer, là où il a des conséquences. */}
                {storedPayroll && (
                  <Alert severity="info" icon={<DescriptionIcon />} sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Chiffres du bulletin <strong>{existingBulletin?.ref_bulletin}</strong>
                      {existingBulletin?.created_at
                        ? ` généré le ${new Date(existingBulletin.created_at).toLocaleDateString('fr-FR')}`
                        : ''}
                      {' — figés, non recalculés. Les modifications faites depuis '}
                      {'(primes, pointage) ne s\'appliqueront qu\'après régénération du mois.'}
                    </Typography>
                    {(existingBulletin as any)?.lines_source === 'derived' && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        Bulletin antérieur au figement des lignes&nbsp;: le détail affiché est
                        reconstruit à partir de la configuration actuelle et peut ne pas
                        correspondre aux totaux.
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Payroll Summary Table */}
                <Typography variant="h6" sx={{ color: HR_COLOR, mb: 2 }}>
                  Récapitulatif de Paie
                </Typography>

                {!displayPayroll ? (
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{
                      mt: 2,
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Configuration de paie requise
                    </Typography>
                    <Typography variant="body2">
                      Cet employé n'a pas encore de configuration de paie enregistrée.
                      Veuillez configurer sa paie via l'onglet <strong>"Paie"</strong> dans la fiche du personnel.
                    </Typography>
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Élément</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Calcul</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Montant</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* A - Salaire de Base */}
                        <TableRow>
                          <TableCell>A - Salaire de Base</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {displayPayroll.heuresPresence !== undefined
                              ? `HP=${Number(displayPayroll.heuresPresence).toFixed(2)}h × (SB=${displayPayroll.salaireBase.toLocaleString()} / 191)`
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {displayPayroll.heuresPresence !== undefined
                              ? formatCurrency(Number(displayPayroll.heuresPresence) * displayPayroll.salaireBase / 191)
                              : formatCurrency(displayPayroll.salaireBase)}
                          </TableCell>
                        </TableRow>

                        {/* Monthly-specific rows: Absence, Jour Férié, Congé */}
                        {displayPayroll.deductionAbsence !== undefined && (
                          <>
                            {displayPayroll.deductionAbsence > 0 && (
                              <TableRow>
                                <TableCell sx={{ pl: 4, color: '#C62828' }}>Absence par Heures</TableCell>
                                <TableCell sx={{ color: '#C62828', fontSize: '0.85rem' }}>
                                  (191 - HP={Number(displayPayroll.heuresPresence || 0).toFixed(2)}h) × (SB={displayPayroll.salaireBase.toLocaleString()} / 191)
                                </TableCell>
                                <TableCell align="right" sx={{ color: '#C62828', fontWeight: 500 }}>
                                  -{formatCurrency(displayPayroll.deductionAbsence)}
                                </TableCell>
                              </TableRow>
                            )}
                            {(displayPayroll.gainJourFerie || 0) > 0 && (
                              <TableRow>
                                <TableCell sx={{ pl: 4, color: '#2E7D32' }}>Jour Férié</TableCell>
                                <TableCell sx={{ color: '#2E7D32', fontSize: '0.85rem' }}>
                                  JF={displayPayroll.joursFerie}j × (SB={displayPayroll.salaireBase.toLocaleString()} / DJ={Number(selectedEmployeeDetails?.declaration_journaliere || 0).toLocaleString()})
                                </TableCell>
                                <TableCell align="right" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                                  +{formatCurrency(displayPayroll.gainJourFerie)}
                                </TableCell>
                              </TableRow>
                            )}
                            {(displayPayroll.gainConge || 0) > 0 && (
                              <TableRow>
                                <TableCell sx={{ pl: 4, color: '#2E7D32' }}>Congé</TableCell>
                                <TableCell sx={{ color: '#2E7D32', fontSize: '0.85rem' }}>
                                  JC={displayPayroll.joursConge}j × (SB={displayPayroll.salaireBase.toLocaleString()} / DJ={Number(selectedEmployeeDetails?.declaration_journaliere || 0).toLocaleString()})
                                </TableCell>
                                <TableCell align="right" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                                  +{formatCurrency(displayPayroll.gainConge)}
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}

                        {/* B - Primes Non Exonérées */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            B - Primes Non Exonérées
                            {displayPayroll.prorationFactor !== undefined && displayPayroll.prorationFactor < 1 && (
                              <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {' '}(proratées à {(displayPayroll.prorationFactor * 100).toFixed(1)}%)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            {displayPayroll.prorationFactor !== undefined && displayPayroll.prorationFactor < 1 && pointageSummary && (() => {
                              const hp = Number(pointageSummary.heures_presence);
                              const hConge = (displayPayroll.joursConge || 0) * 7.3461;
                              const hFerie = (displayPayroll.joursFerie || 0) * 7.3461;
                              const total = hp + hConge + hFerie;
                              return `(HT=${hp.toFixed(2)}h + JC=${hConge.toFixed(2)}h + JF=${hFerie.toFixed(2)}h) / 191 = ${total.toFixed(2)} / 191 = ${(displayPayroll.prorationFactor * 100).toFixed(1)}%`;
                            })()}
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                        {displayPayroll.primesNonExonereesDetails.map((prime, index) => (
                          <TableRow key={`pne-${index}`}>
                            <TableCell sx={{ pl: 4, color: 'text.secondary' }}>• {prime.nom}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                              {prime.prorate === false
                                ? 'Montant fixe'
                                : displayPayroll.prorationFactor !== undefined && displayPayroll.prorationFactor < 1
                                ? `× ${(displayPayroll.prorationFactor * 100).toFixed(1)}%`
                                : '—'}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(prime.montant)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, pl: 2 }}>Total Primes Non Exonérées</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>Σ primes</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(displayPayroll.totalPrimesNonExonerees)}</TableCell>
                        </TableRow>

                        {/* C - Primes Exonérées */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            C - Primes Exonérées
                            {displayPayroll.prorationFactor !== undefined && displayPayroll.prorationFactor < 1 && (
                              <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {' '}(proratées à {(displayPayroll.prorationFactor * 100).toFixed(1)}%)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                            {displayPayroll.prorationFactor !== undefined && displayPayroll.prorationFactor < 1 && pointageSummary && (() => {
                              const hp = Number(pointageSummary.heures_presence);
                              const hConge = (displayPayroll.joursConge || 0) * 7.3461;
                              const hFerie = (displayPayroll.joursFerie || 0) * 7.3461;
                              const total = hp + hConge + hFerie;
                              return `(HT=${hp.toFixed(2)}h + JC=${hConge.toFixed(2)}h + JF=${hFerie.toFixed(2)}h) / 191 = ${total.toFixed(2)} / 191 = ${(displayPayroll.prorationFactor * 100).toFixed(1)}%`;
                            })()}
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                        {displayPayroll.primesExonereesDetails.map((prime, index) => (
                          <TableRow key={`pe-${index}`}>
                            <TableCell sx={{ pl: 4, color: 'text.secondary' }}>• {prime.nom}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                              {prime.prorate === false
                                ? 'Montant fixe'
                                : displayPayroll.prorationFactor !== undefined && displayPayroll.prorationFactor < 1
                                ? `× ${(displayPayroll.prorationFactor * 100).toFixed(1)}%`
                                : '—'}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(prime.montant)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, pl: 2 }}>Total Primes Exonérées</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>Σ primes</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(displayPayroll.totalPrimesExonerees)}</TableCell>
                        </TableRow>

                        {/* Prime d'Ancienneté */}
                        {displayPayroll.primeAnciennete !== undefined && (
                          <TableRow>
                            <TableCell sx={{ color: HR_COLOR }}>
                              Prime d'Ancienneté ({((displayPayroll.tauxAnciennete || 0) * 100).toFixed(0)}%)
                            </TableCell>
                            <TableCell sx={{ color: HR_COLOR, fontSize: '0.85rem' }}>
                              {displayPayroll.heuresPresence !== undefined
                                ? `(A=${(Number(displayPayroll.heuresPresence) * displayPayroll.salaireBase / 191).toLocaleString(undefined, {maximumFractionDigits: 2})} + JF=${displayPayroll.gainJourFerie?.toLocaleString() || 0} + JC=${displayPayroll.gainConge?.toLocaleString() || 0}) × ${((displayPayroll.tauxAnciennete || 0) * 100).toFixed(0)}%`
                                : `(SB=${displayPayroll.salaireBase.toLocaleString()} + JF=${displayPayroll.gainJourFerie?.toLocaleString() || 0} + JC=${displayPayroll.gainConge?.toLocaleString() || 0}) × ${((displayPayroll.tauxAnciennete || 0) * 100).toFixed(0)}%`
                              }
                            </TableCell>
                            <TableCell align="right" sx={{ color: HR_COLOR, fontWeight: 500 }}>
                              {displayPayroll.primeAnciennete > 0 ? '+' : ''}{formatCurrency(displayPayroll.primeAnciennete)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* D - Salaire Brut Global */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>D - Salaire Brut Global</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {displayPayroll.heuresPresence !== undefined
                              ? `A=${(Number(displayPayroll.heuresPresence) * displayPayroll.salaireBase / 191).toLocaleString(undefined, {maximumFractionDigits: 2})} + JF=${(displayPayroll.gainJourFerie || 0).toLocaleString()} + JC=${(displayPayroll.gainConge || 0).toLocaleString()} + B=${displayPayroll.totalPrimesNonExonerees.toLocaleString()} + C=${displayPayroll.totalPrimesExonerees.toLocaleString()} + Anc=${(displayPayroll.primeAnciennete || 0).toLocaleString()}`
                              : `A=${displayPayroll.salaireBase.toLocaleString()} + B=${displayPayroll.totalPrimesNonExonerees.toLocaleString()} + C=${displayPayroll.totalPrimesExonerees.toLocaleString()}`
                            }
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(displayPayroll.salaireBrutGlobal)}</TableCell>
                        </TableRow>

                        {/* E - Salaire Brut Imposable */}
                        <TableRow>
                          <TableCell>E - Salaire Brut Imposable</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            D={displayPayroll.salaireBrutGlobal.toLocaleString()} - C={displayPayroll.totalPrimesExonerees.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(displayPayroll.salaireBrutImposable)}</TableCell>
                        </TableRow>

                        {/* F - Cotisations Sociales */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>F - Cotisations Sociales</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}></TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                        {displayPayroll.cotisationsDetails.map((cotisation, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ pl: 4, color: 'text.secondary' }}>• {cotisation.nom}</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                              {cotisation.isCNSS
                                ? `MIN(E=${displayPayroll.salaireBrutImposable.toLocaleString()}, ${CNSS_CEILING.toLocaleString()}) = ${cotisation.baseForCalculation.toLocaleString()} × ${cotisation.taux.toFixed(2)}%`
                                : `E=${displayPayroll.salaireBrutImposable.toLocaleString()} × ${cotisation.taux.toFixed(2)}%`}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#f44336' }}>-{formatCurrency(cotisation.montant)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, pl: 2 }}>Total Cotisations</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            Σ ({displayPayroll.cotisationsDetails.reduce((sum, c) => sum + c.taux, 0).toFixed(2)}%)
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#f44336', fontWeight: 600 }}>-{formatCurrency(displayPayroll.totalCotisations)}</TableCell>
                        </TableRow>

                        {/* G - Frais Professionnel */}
                        <TableRow>
                          <TableCell>G - Frais Professionnel</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            MIN(E={displayPayroll.salaireBrutImposable.toLocaleString()} × {(displayPayroll.fraisProfessionnelRate * 100).toFixed(0)}%, {displayPayroll.fraisProfessionnelMax.toLocaleString()})
                          </TableCell>
                          <TableCell align="right">{formatCurrency(displayPayroll.fraisProfessionnel)}</TableCell>
                        </TableRow>

                        {/* H - Salaire Net Imposable */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>H - Salaire Net Imposable</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            E={displayPayroll.salaireBrutImposable.toLocaleString()} - F={displayPayroll.totalCotisations.toLocaleString()} - G={displayPayroll.fraisProfessionnel.toLocaleString()}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(displayPayroll.salaireNetImposable)}</TableCell>
                        </TableRow>

                        {/* I - IR Brut */}
                        <TableRow>
                          <TableCell>
                            I - IR Brut
                            {displayPayroll.irBracket && <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}> ({displayPayroll.irBracket.rate * 100}%)</Typography>}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                            {displayPayroll.irBracket
                              ? `(H=${displayPayroll.salaireNetImposable.toLocaleString()} × ${displayPayroll.irBracket.rate * 100}%) - ${displayPayroll.irBracket.deduction.toLocaleString()}`
                              : '—'}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(displayPayroll.irBrut)}</TableCell>
                        </TableRow>

                        {/* J - Déduction Charge Famille */}
                        <TableRow>
                          <TableCell>J - Déduction Charge Famille</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {displayPayroll.nbrPersonnesACharge} pers. × 41,67 DH (max 250)
                          </TableCell>
                          <TableCell align="right">{formatCurrency(displayPayroll.deductionChargeFamille)}</TableCell>
                        </TableRow>

                        {/* K - IR Net */}
                        <TableRow>
                          <TableCell>K - IR Net</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            I={displayPayroll.irBrut.toLocaleString()} - J={displayPayroll.deductionChargeFamille.toLocaleString()}
                          </TableCell>
                          <TableCell align="right" sx={{ color: displayPayroll.ir > 0 ? '#f44336' : 'inherit' }}>{displayPayroll.ir > 0 ? `-${formatCurrency(displayPayroll.ir)}` : formatCurrency(displayPayroll.ir)}</TableCell>
                        </TableRow>

                        {/* Active Loan Section */}
                        {activePret && (
                          <TableRow sx={{
                            opacity: !activePret.inclus_dans_calcul ? 0.5 : 1,
                            bgcolor: !activePret.inclus_dans_calcul ? 'background.default' : 'inherit',
                          }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                L - Prêt en cours
                                <Chip
                                  label={activePret.ref_pret || `#${activePret.pret_id}`}
                                  size="small"
                                  sx={{ bgcolor: activePret.inclus_dans_calcul ? '#1976D2' : '#BDBDBD', color: 'white', fontSize: '0.7rem' }}
                                />
                                {activePret.is_paid ? (
                                  <Tooltip title="Payé ce mois">
                                    <PaidIcon fontSize="small" sx={{ color: '#4CAF50', ml: 1 }} />
                                  </Tooltip>
                                ) : existingBulletin ? (
                                  <Tooltip title={activePret.inclus_dans_calcul ? 'Inclus dans le bulletin' : 'Non inclus dans le bulletin'}>
                                    <Chip
                                      label={activePret.inclus_dans_calcul ? 'Inclus' : 'Exclu'}
                                      size="small"
                                      sx={{
                                        bgcolor: activePret.inclus_dans_calcul ? '#4CAF50' : '#9E9E9E',
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        ml: 1,
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={activePret.inclus_dans_calcul ? 'Inclus dans le calcul — cliquer pour exclure' : 'Exclu du calcul — cliquer pour inclure'}>
                                    <Switch
                                      size="small"
                                      checked={activePret.inclus_dans_calcul}
                                      onChange={() => handleTogglePret(activePret.pret_id, activePret.inclus_dans_calcul)}
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: HR_COLOR },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: HR_COLOR },
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              Mensualité ({activePret.mois_restants} mois restants)
                            </TableCell>
                            <TableCell align="right" sx={{
                              color: activePret.inclus_dans_calcul ? '#f44336' : '#BDBDBD',
                              textDecoration: !activePret.inclus_dans_calcul ? 'line-through' : 'none',
                            }}>
                              -{formatCurrency(Number(activePret.montant_mensuel))}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Active Acomptes Section */}
                        {activeAcomptes.map((acompte, index) => (
                          <TableRow
                            key={acompte.acompte_id}
                            sx={{
                              opacity: !acompte.inclus_dans_calcul ? 0.5 : 1,
                              bgcolor: !acompte.inclus_dans_calcul ? 'background.default' : 'inherit',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {activeAcomptes.length > 1 ? `M${index + 1}` : 'M'} - Acompte
                                <Chip
                                  label={acompte.ref_acompte || `#${acompte.acompte_id}`}
                                  size="small"
                                  sx={{ bgcolor: acompte.inclus_dans_calcul ? '#FF9800' : '#BDBDBD', color: 'white', fontSize: '0.7rem' }}
                                />
                                {acompte.is_paid ? (
                                  <Tooltip title="Acompte payé">
                                    <PaidIcon fontSize="small" sx={{ color: '#4CAF50', ml: 1 }} />
                                  </Tooltip>
                                ) : existingBulletin ? (
                                  <Tooltip title={acompte.inclus_dans_calcul ? 'Inclus dans le bulletin' : 'Non inclus dans le bulletin'}>
                                    <Chip
                                      label={acompte.inclus_dans_calcul ? 'Inclus' : 'Exclu'}
                                      size="small"
                                      sx={{
                                        bgcolor: acompte.inclus_dans_calcul ? '#4CAF50' : '#9E9E9E',
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        ml: 1,
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <>
                                    <Tooltip title={acompte.inclus_dans_calcul ? 'Inclus dans le calcul — cliquer pour exclure' : 'Exclu du calcul — cliquer pour inclure'}>
                                      <Switch
                                        size="small"
                                        checked={acompte.inclus_dans_calcul}
                                        onChange={() => handleToggleAcompte(acompte.acompte_id, acompte.inclus_dans_calcul)}
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked': { color: HR_COLOR },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: HR_COLOR },
                                        }}
                                      />
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              Demandé le {new Date(acompte.date_demande).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell align="right" sx={{
                              color: acompte.inclus_dans_calcul ? '#f44336' : '#BDBDBD',
                              textDecoration: !acompte.inclus_dans_calcul ? 'line-through' : 'none',
                            }}>
                              -{formatCurrency(Number(acompte.montant))}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* NET TO PAY */}
                        <TableRow sx={{ backgroundColor: HR_COLOR }}>
                          <TableCell sx={{ py: 1.5, fontWeight: 700, color: 'white', fontSize: '1rem' }}>
                            NET A PAYER
                          </TableCell>
                          <TableCell sx={{ py: 1.5, color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                            D={displayPayroll.salaireBrutGlobal.toLocaleString()} - F={displayPayroll.totalCotisations.toLocaleString()} - K={displayPayroll.ir.toLocaleString()}
                            {activePret?.inclus_dans_calcul && ` - L=${Number(activePret.montant_mensuel).toLocaleString()}`}
                            {activeAcomptes.filter(a => a.inclus_dans_calcul).map((a, i) => ` - ${activeAcomptes.filter(x => x.inclus_dans_calcul).length > 1 ? `M${i + 1}` : 'M'}=${Number(a.montant).toLocaleString()}`)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.5, fontWeight: 700, color: 'white', fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                            {formatCurrency(
                              displayPayroll.netAPayer
                              - (activePret?.inclus_dans_calcul ? Number(activePret.montant_mensuel) : 0)
                              - activeAcomptes.filter(a => a.inclus_dans_calcul).reduce((sum, a) => sum + Number(a.montant), 0)
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Pre-check Dialog */}
      <Dialog
        open={showPreCheckDialog}
        onClose={() => setShowPreCheckDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Génération des Bulletins — {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {preCheckData && (
            <Box>
              {/* Summary counts */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
                <Box sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#E8F5E9',
                  border: '1px solid #A5D6A7',
                  textAlign: 'center',
                }}>
                  <ReadyIcon sx={{ color: '#2E7D32', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#2E7D32' }}>
                    {preCheckData.ready}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#2E7D32' }}>
                    Prêt(s)
                  </Typography>
                </Box>
                <Box sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#FFF3E0',
                  border: '1px solid #FFCC80',
                  textAlign: 'center',
                }}>
                  <ErrorOutlineIcon sx={{ color: '#E65100', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#E65100' }}>
                    {preCheckData.not_ready}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#E65100' }}>
                    En attente
                  </Typography>
                </Box>
                {preCheckData.already_exists > 0 && (
                  <Box sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    border: 1, borderColor: 'divider',
                    textAlign: 'center',
                  }}>
                    <DescriptionIcon sx={{ color: '#757575', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#757575' }}>
                      {preCheckData.already_exists}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#757575' }}>
                      Déjà existant(s)
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Expandable details for not-ready employees */}
              {preCheckData.not_ready > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setPreCheckExpanded(!preCheckExpanded)}
                    endIcon={preCheckExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: '#E65100', textTransform: 'none', fontWeight: 500 }}
                  >
                    {preCheckExpanded ? 'Masquer' : 'Voir'} les détails ({preCheckData.not_ready} employé(s))
                  </Button>
                  <Collapse in={preCheckExpanded}>
                    <Box sx={{
                      maxHeight: 250,
                      overflow: 'auto',
                      mt: 1,
                      borderRadius: 1,
                      border: 1, borderColor: 'divider',
                    }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'background.default' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>Employé</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>Matricule</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>Raison</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {preCheckData.details
                            .filter(d => d.status === 'error')
                            .map(d => (
                              <TableRow key={d.employee_id}>
                                <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>{d.employee_name}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>{d.matricule}</TableCell>
                                <TableCell sx={{ fontSize: '0.8rem', py: 0.5, color: '#E65100' }}>{d.reason}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowPreCheckDialog(false)}
            sx={{ textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGenerate}
            disabled={!preCheckData || preCheckData.ready === 0}
            sx={{
              bgcolor: HR_COLOR,
              '&:hover': { bgcolor: '#00695C' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Générer ({preCheckData?.ready || 0})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default BaseCalculPaieManagement;
